import * as React from 'react';
import { useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import { TextFieldVariants } from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Checkbox, FormControlLabel, Stack } from '@mui/material';
import {
    useActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/useActionConfirmationDialog";
import { useAppContext } from "../../../../shared/contexts/app-context/components/AppContext";
import { PortfolioSettingsModel } from "../../../portfolio-info/types/PortfolioSettingsModel";
import {
    ActionConfirmationDialog
} from "../../../../shared/components/action-confirmation-dialog/ActionConfirmationDialog";
import { numberFormatters } from "../../../../screens/shared/utils/numberFormatters";
import { NumericField } from "../../../../screens/positions-and-orders/controls";
import { DraggableDialogPaper } from "../../../../screens/positions-and-orders/dialogs/DraggableDialogPaper";
import { DraggableDialogTitle } from "../../../../screens/positions-and-orders/dialogs/DraggableDialogTitle";
import { mathCalculationService } from "../../../../shared/services/MathCalculationService";

export function ChartSettingsDialog({
    isOpen,
    setIsOpen,
    submitAction,
    editingModel,
    icon,
}: {
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>,
    submitAction: ((portfolio: PortfolioSettingsModel) => void) | undefined,
    editingModel?: PortfolioSettingsModel | null,
    handleMinUnderlyingPriceChanging?: string,
    icon: React.ReactNode;
}) {
    const {
        marketDataDto,
        selectedPortfolioBalance,
    } = useAppContext();
    
    const {
        isActionConfirmationDialogOpen,
        setIsActionConfirmationDialogOpen,
        confirmedAction,
        setConfirmedAction,
        confirmationMessage,
        setConfirmationMessage,
        submitButtonName,
        setSubmitButtonName,
    } = useActionConfirmationDialog();
    
    const componentVariantValue: TextFieldVariants | undefined = 'standard';
    
    const [useOffset, setUseOffset] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [calculationStep, setCalculationStep] = useState<number>(0);
    const [minUnderlyingPrice, setMinUnderlyingPrice] = useState<number>(0);
    const [maxUnderlyingPrice, setMaxUnderlyingPrice] = useState<number>(0);

    const [useBalanceRangeRestriction, setUseBalanceRangeRestriction] = useState<boolean>(false);
    const [minBalance, setMinBalance] = useState<number>(0);
    const [maxBalance, setMaxBalance] = useState<number>(0);
    
    const [hideCommitedBalance, setHideCommitedBalance] = useState<boolean>(false);
    const [commitedBalance, setCommitedBalance] = useState<number>(0);

    const [showTradeSessionBalanceLine, setShowTradeSessionBalanceLine] = useState<boolean>(false);
    const [tradeSessionBalance, setTradeSessionBalance] = useState<number>(0);
    
    const [showSupportLine, setShowSupportLine] = useState<boolean>(false);
    const [supportLinePrice, setSupportLinePrice] = useState<number>(0);
    const [showResistanceLine, setShowResistanceLine] = useState<boolean>(false);
    const [resistanceLinePrice, setResistanceLinePrice] = useState<number>(0);
    
    useEffect(() => {
        if (editingModel === undefined || editingModel === null) {
            return;
        }
        
        setUseOffset(editingModel.useOffset);
        setOffset(editingModel.offset);
        setCalculationStep(editingModel.calculationStep);
        setMinUnderlyingPrice(editingModel.minUnderlyingPrice);
        setMaxUnderlyingPrice(editingModel.maxUnderlyingPrice);
        
        setUseBalanceRangeRestriction(editingModel.useBalanceRangeRestriction);
        setMinBalance(editingModel.minBalance);
        setMaxBalance(editingModel.maxBalance);
        
        setHideCommitedBalance(editingModel.hideCommitedBalance);
        setCommitedBalance(editingModel.commitedBalance);

        setShowTradeSessionBalanceLine(editingModel.showTradeSessionBalanceLine);
        setTradeSessionBalance(editingModel.tradeSessionBalance);
        
        setShowSupportLine(editingModel.showSupportLine);
        setSupportLinePrice(editingModel.supportLinePrice);
        setShowResistanceLine(editingModel.showResistanceLine);
        setResistanceLinePrice(editingModel.resistanceLinePrice);
    }, [editingModel]);
    
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        
        const formData = new FormData(event.currentTarget);
        const data = Object.fromEntries((formData as any).entries());

        const updatedPortfolioSettingsModel = {
            portfolioId: 0,
            useOffset: useOffset,
            offset: offset,
            minUnderlyingPrice: minUnderlyingPrice,
            maxUnderlyingPrice: maxUnderlyingPrice,
            useBalanceRangeRestriction: useBalanceRangeRestriction,
            minBalance: minBalance,
            maxBalance: maxBalance,
            calculationStep: calculationStep,
            selectedOptionParameterType: editingModel!.selectedOptionParameterType,
            selectedTableInfoType: editingModel!.selectedTableInfoType,
            selectedProfileTypes: [...editingModel!.selectedProfileTypes],
            selectedCustomDate: editingModel!.selectedCustomDate,
            hideCommitedBalance: hideCommitedBalance,
            commitedBalance: commitedBalance,
            showTradeSessionBalanceLine: showTradeSessionBalanceLine,
            tradeSessionBalance: tradeSessionBalance,
            showSupportLine: showSupportLine,
            supportLinePrice: supportLinePrice,
            showResistanceLine: showResistanceLine,
            resistanceLinePrice: resistanceLinePrice,
            useCustomPrice: editingModel!.useCustomPrice,
            customPrice: editingModel!.customPrice,
            customPriceStep: editingModel!.customPriceStep,
            useComparedPortfolio: editingModel!.useComparedPortfolio,
            comparedPortfolioId: editingModel!.comparedPortfolioId,
            showDeltaNeutralPriceLines: editingModel!.showDeltaNeutralPriceLines,
            showPortfolioPerformance: editingModel!.showPortfolioPerformance,
            usePortfolioAnnualisedRoi: editingModel!.usePortfolioAnnualisedRoi,
            showDeltaValues: editingModel!.showDeltaValues,
            balanceStartValueX: editingModel!.balanceStartValueX,
            balanceEndValueX: editingModel!.balanceEndValueX,
            balanceStartValueY: editingModel!.balanceStartValueY,
            balanceEndValueY: editingModel!.balanceEndValueY,
            isTripleCustomBalanceEnabled: editingModel!.isTripleCustomBalanceEnabled,
            isTripleCustomBalanceUsingEqualShifts: editingModel!.isTripleCustomBalanceUsingEqualShifts,
            isCustomImpliedVolatilityEnabled: editingModel!.isCustomImpliedVolatilityEnabled,
            showClosedPositions: editingModel!.showClosedPositions,
            showLockedOrders: editingModel!.showLockedOrders,
        };

        if (submitAction) {
            submitAction(updatedPortfolioSettingsModel);
            handleClose();
        }
    };

    const handleUseOffsetChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setUseOffset(arg.target.checked)
        
        if (marketDataDto === undefined) {
            return;
        }
        
        const underlyingPrice = marketDataDto.UnderlyingPrice;

        if (underlyingPrice === null) {
            return;
        }

        setMinUnderlyingPrice(underlyingPrice - offset);
        setMaxUnderlyingPrice(underlyingPrice + offset);
    }
    
    const handleOffsetChanging = (newValue: number): void => {
        if (marketDataDto === undefined) {
            return;
        }
        
        setOffset(newValue);
        
        const underlyingPrice = marketDataDto.UnderlyingPrice;
        
        if (underlyingPrice === null) {
            return;
        }
        
        setMinUnderlyingPrice(mathCalculationService.round((underlyingPrice - newValue), 0));
        setMaxUnderlyingPrice(mathCalculationService.round((underlyingPrice + newValue), 0));
    }

    const handleCalculationStepChanging = (newValue: number): void => {
        setCalculationStep(newValue);
    }
    
    const handleMinUnderlyingPriceChanging = (newValue: number): void => {
        setMinUnderlyingPrice(newValue);
    }

    const handleMaxUnderlyingPriceChanging = (newValue: number): void => {
        setMaxUnderlyingPrice(newValue);
    }

    const handleUseBalanceRangeRestrictionChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setUseBalanceRangeRestriction(arg.target.checked)
    }
    
    const handleMinBalanceChanging = (newValue: number): void => {
        setMinBalance(newValue);
    }

    const handleMaxBalanceChanging = (newValue: number): void => {
        setMaxBalance(newValue);
    }
    
    const handleHideCommitedBalanceChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setHideCommitedBalance(arg.target.checked)
    }

    const handleCommitSessionBalanceClick = (): void => {
        setCommitedBalance(selectedPortfolioBalance);        
    }

    const handleCommitedBalanceChanging = (newValue: number): void => {
        setCommitedBalance(newValue);
    }

    const handleShowTradeSessionBalanceLineChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setShowTradeSessionBalanceLine(arg.target.checked)
    }
    
    const handleTradeSessionBalanceChanging = (newValue: number): void => {
        setTradeSessionBalance(newValue);
    }
    
    const handleShowSupportLineChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setShowSupportLine(arg.target.checked)
    }

    const handleShowResistanceLineChanging = (arg: React.ChangeEvent<HTMLInputElement>): void => {
        setShowResistanceLine(arg.target.checked)
    }

    const handleSupportLinePriceChanging = (newValue: number): void => {
        setSupportLinePrice(newValue);
    }

    const handleResistanceLinePriceChanging = (newValue: number): void => {
        setResistanceLinePrice(newValue);
    }
    
    return (
        <>
            <ActionConfirmationDialog
                isOpen={isActionConfirmationDialogOpen}
                setIsOpen={setIsActionConfirmationDialogOpen}
                confirmationMessage={confirmationMessage}
                confirmedParentAction={confirmedAction}
                submitButtonName={submitButtonName}
                doAfterConfirmation={handleClose}
            />
            <Dialog
                open={isOpen}
                onClose={handleClose}
                PaperComponent={DraggableDialogPaper}
                PaperProps={{
                    component: 'form',
                    onSubmit: handleSubmit,
                }}
            >
                <DraggableDialogTitle
                    icon={icon}
                    title="Edit portfolio settings"
                    onClose={handleClose}
                />
                <DialogContent>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                    color="default"
                                    id="useOffset"
                                    name="useOffset"
                                    checked={useOffset}
                                    onChange={handleUseOffsetChanging}
                                />
                            }
                            label="Use offset from the current underlying price"
                            sx={{ padding: 0, margin: 0 }}
                        />
                        <Stack
                            direction="row"
                            spacing={2}
                        >
                            <NumericField
                                id="calculationStep"
                                name="calculationStep"
                                label="Step"
                                value={calculationStep}
                                onValueChange={handleCalculationStepChanging}
                                formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                width={55}
                            />
                            <NumericField
                                id="offset"
                                name="offset"
                                label="Offset"
                                value={offset}
                                onValueChange={handleOffsetChanging}
                                formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                width={70}
                                disabled={!useOffset}
                            />
                            <NumericField
                                id="minUnderlyingPrice"
                                name="minUnderlyingPrice"
                                label="Min price"
                                value={minUnderlyingPrice}
                                onValueChange={handleMinUnderlyingPriceChanging}
                                formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                width={90}
                                disabled={useOffset}
                            />
                            <NumericField
                                id="maxUnderlyingPrice"
                                name="maxUnderlyingPrice"
                                label="Max price"
                                value={maxUnderlyingPrice}
                                onValueChange={handleMaxUnderlyingPriceChanging}
                                formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                width={90}
                                disabled={useOffset}
                            />
                        </Stack>
                        <Stack direction="column" spacing={2}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                        color="default"
                                        id="useBalanceRangeRestriction"
                                        name="useBalanceRangeRestriction"
                                        checked={useBalanceRangeRestriction}
                                        onChange={handleUseBalanceRangeRestrictionChanging}
                                    />
                                }
                                label="Use balance range restriction"
                                sx={{ padding: 0, margin: 0 }}
                            />
                            <Stack
                                direction="row"
                                spacing={2}
                            >
                                <NumericField
                                    id="minBalance"
                                    name="minBalance"
                                    label="Min balance"
                                    value={minBalance}
                                    onValueChange={handleMinBalanceChanging}
                                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                    width={70}
                                    disabled={!useBalanceRangeRestriction}
                                />
                                <NumericField
                                    id="maxBalance"
                                    name="maxBalance"
                                    label="Max balance"
                                    value={maxBalance}
                                    onValueChange={handleMaxBalanceChanging}
                                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                    width={70}
                                    disabled={!useBalanceRangeRestriction}
                                />
                            </Stack>
                        </Stack>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleCommitSessionBalanceClick}
                            sx={{
                                backgroundColor: theme => theme.palette.primary.light,
                                "&:hover": {
                                    backgroundColor: theme => theme.palette.primary.main,
                                },
                            }}
                            disabled={!hideCommitedBalance}
                        >
                            Commit session balance
                        </Button>
                        <Stack direction="row" spacing={2}>
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                            color="default"
                                            id="hideCommitedBalance"
                                            name="hideCommitedBalance"
                                            checked={hideCommitedBalance}
                                            onChange={handleHideCommitedBalanceChanging}
                                        />
                                    }
                                    label="Hide commited balance"
                                    sx={{ padding: 0, margin: 0 }}
                                />
                                <NumericField
                                    id="commitedBalance"
                                    name="commitedBalance"
                                    label="Commited balance"
                                    value={commitedBalance}
                                    onValueChange={handleCommitedBalanceChanging}
                                    formatter={(value: number) => numberFormatters.format2(value, { useGroupFormat: false })}
                                    width={135}
                                    step={0.01}
                                    disabled={!hideCommitedBalance}
                                />
                            </Stack>
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                            color="default"
                                            id="showTradeSessionBalanceLine"
                                            name="showTradeSessionBalanceLine"
                                            checked={showTradeSessionBalanceLine}
                                            onChange={handleShowTradeSessionBalanceLineChanging}
                                        />
                                    }
                                    label="Show trade session balance line"
                                    sx={{ padding: 0, margin: 0 }}
                                />
                                <NumericField
                                    id="tradeSessionBalance"
                                    name="tradeSessionBalance"
                                    label="Trade session balance"
                                    value={tradeSessionBalance}
                                    onValueChange={handleTradeSessionBalanceChanging}
                                    formatter={(value: number) => numberFormatters.format2(value, { useGroupFormat: false })}
                                    width={135}
                                    step={0.01}
                                    disabled={!showTradeSessionBalanceLine}
                                />
                            </Stack>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                            color="default"
                                            id="showSupportLine"
                                            name="showSupportLine"
                                            checked={showSupportLine}
                                            onChange={handleShowSupportLineChanging}
                                        />
                                    }
                                    label="Show support line"
                                    sx={{ padding: 0, margin: 0 }}
                                />
                                <NumericField
                                    id="supportLinePrice"
                                    name="supportLinePrice"
                                    label="Support price"
                                    value={supportLinePrice}
                                    onValueChange={handleSupportLinePriceChanging}
                                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                    width={120}
                                    disabled={!showSupportLine}
                                />
                            </Stack>
                            <Stack direction="column" spacing={1}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            sx={{ padding: 0, marginLeft: -0.5, marginRight: 0.5 }}
                                            color="default"
                                            id="showResistanceLine"
                                            name="showResistanceLine"
                                            checked={showResistanceLine}
                                            onChange={handleShowResistanceLineChanging}
                                        />
                                    }
                                    label="Show resistance line"
                                    sx={{ padding: 0, margin: 0 }}
                                />
                                <NumericField
                                    id="resistanceLinePrice"
                                    name="resistanceLinePrice"
                                    label="Resistance line price"
                                    value={resistanceLinePrice}
                                    onValueChange={handleResistanceLinePriceChanging}
                                    formatter={(value: number) => numberFormatters.format0(value, numberFormatters.hideGroupDelimiterFormatOptions)}
                                    width={120}
                                    disabled={!showResistanceLine}
                                />
                            </Stack>
                        </Stack>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ fontWeight: "bold" }}
                        size="small"
                        variant="contained"
                        color="primary"
                        type="submit"
                    >
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
